import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { THEMES } from "./constants/themes";
import { DISPLAY_SERIF, SANS } from "./constants/ui";
import { DEFAULT_THEME_KEY, readStoredTheme } from "./themePreference";

// *** REPLACE WITH YOUR INVITE CODE ***
const INVITE_CODE = "SHELFIE26";
// *************************************

const T = {
  bg: "#F7F4EF", surface: "#FFFFFF", s2: "#F0EDE8",
  border: "#E4DED6", text: "#1C1917", sub: "#78716C", mute: "#A8A29E",
  accent: "#2B4F82", accentBg: "rgba(43,79,130,0.08)",
};

const inp = {
  width: "100%", padding: "12px 14px", borderRadius: 10,
  border: "1px solid " + T.border, background: T.s2,
  color: T.text, fontFamily: SANS, fontSize: 14,
  outline: "none", marginBottom: 10, boxSizing: "border-box",
};

export default function AuthGate({ children }) {
  const [bootTheme] = useState(
    () =>
      THEMES[readStoredTheme(THEMES, DEFAULT_THEME_KEY)] ||
      THEMES[DEFAULT_THEME_KEY],
  );
  const [user,     setUser]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [mode,     setMode]     = useState("login"); // "login" | "invite" | "signup"
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [invite,   setInvite]   = useState("");
  const [err,      setErr]      = useState("");
  const [msg,      setMsg]      = useState("");
  const [busy,     setBusy]     = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  if (loading) return (
    <div style={{ fontFamily: SANS, fontVariantNumeric: "lining-nums", fontOpticalSizing: "auto", background: bootTheme.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ fontFamily: DISPLAY_SERIF, fontSize: 28, color: bootTheme.text }}>Loading...</div>
      <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid " + bootTheme.border, borderTopColor: bootTheme.accent, animation: "spin 0.7s linear infinite" }}></div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (user) return <>{children}</>;

  async function handleLogin() {
    setErr(""); setMsg(""); setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      setErr(e.code === "auth/invalid-credential" || e.code === "auth/wrong-password"
        ? "Incorrect email or password."
        : e.code === "auth/user-not-found"
        ? "No account found with that email."
        : e.code === "auth/invalid-email"
        ? "Please enter a valid email address."
        : "Login failed. Please try again.");
    }
    setBusy(false);
  }

  function handleInvite() {
    setErr("");
    setMsg("");
    if (invite.trim().toUpperCase() !== INVITE_CODE.toUpperCase()) {
      setErr("Invalid invite code. Please check and try again.");
      return;
    }
    setMode("signup");
  }

  async function handleSignUp() {
    setErr("");
    setMsg("");
    if (!name.trim()) { setErr("Please enter your name."); return; }
    if (!email.trim()) { setErr("Please enter your email."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      const cleanName = name.trim();
      const cleanEmail = email.trim().toLowerCase();
      const inviteCode = invite.trim().toUpperCase();
      const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
      await updateProfile(cred.user, { displayName: cleanName });
      await setDoc(
        doc(db, "users", cred.user.uid, "profile", "account"),
        {
          uid: cred.user.uid,
          name: cleanName,
          email: cleanEmail,
          signupInviteCode: inviteCode,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (e) {
      setErr(e.code === "auth/email-already-in-use"
        ? "An account with this email already exists."
        : e.code === "auth/invalid-email"
        ? "Please enter a valid email address."
        : e.code === "auth/weak-password"
        ? "Password is too weak. Use at least 6 characters."
        : "Sign up failed. Please try again.");
    }
    setBusy(false);
  }

  async function handleForgotPassword() {
    setErr("");
    setMsg("");
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setErr("Enter your email above first, then tap Forgot password.");
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      setMsg("Password reset email sent. Check your inbox.");
    } catch (e) {
      if (e.code === "auth/invalid-email") {
        setErr("Please enter a valid email address.");
      } else if (e.code === "auth/user-not-found") {
        setMsg("If an account exists for that email, a reset link is on the way.");
      } else {
        setErr("Couldn't send reset email. Please try again.");
      }
    }
    setBusy(false);
  }

  return (
    <div style={{ fontFamily: SANS, fontVariantNumeric: "lining-nums", fontOpticalSizing: "auto", background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bitter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        *{font-optical-sizing:auto}
        input::placeholder{color:#A8A29E}
        input:focus{border-color:#2B4F82 !important; background:#fff !important}
      `}</style>

      <div style={{ background: T.surface, borderRadius: 20, padding: "32px 28px", width: "100%", maxWidth: 380, boxShadow: "0 8px 40px rgba(0,0,0,0.08)", border: "1px solid " + T.border }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📚</div>
          <div style={{ fontFamily: DISPLAY_SERIF, fontSize: 28, color: T.text, marginBottom: 6 }}>
            {mode === "login"  ? "Welcome back"      :
             mode === "invite" ? "Enter invite code" :
             "Create account"}
          </div>
          <div style={{ fontSize: 13, color: T.mute }}>
            {mode === "login"  ? "Sign in to your library"         :
             mode === "invite" ? "You need an invite code to join" :
             "Almost there — set up your account"}
          </div>
        </div>

        {/* LOGIN */}
        {mode === "login" && (
          <div>
            <input style={inp} placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()}/>
            <input style={inp} placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()}/>
            <div style={{ display: "flex", justifyContent: "flex-end", margin: "-2px 0 12px" }}>
              <button
                type="button"
                disabled={busy}
                onClick={handleForgotPassword}
                style={{
                  background: "none",
                  border: "none",
                  cursor: busy ? "default" : "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  color: busy ? T.mute : T.accent,
                  fontFamily: SANS,
                  padding: 0,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                Forgot password?
              </button>
            </div>
            {err && <div style={{ color: "#C4584A", fontSize: 13, marginBottom: 10, padding: "8px 12px", background: "rgba(196,88,74,0.08)", borderRadius: 8 }}>{err}</div>}
            {msg && <div style={{ color: "#4E8060", fontSize: 13, marginBottom: 10, padding: "8px 12px", background: "rgba(78,128,96,0.08)", borderRadius: 8 }}>{msg}</div>}
            <button disabled={busy} onClick={handleLogin} style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", background: T.accent, color: "#fff", fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", fontFamily: SANS, opacity: busy ? 0.7 : 1 }}>
              {busy ? "Signing in..." : "Sign In"}
            </button>
            <div style={{ textAlign: "center", marginTop: 18 }}>
              <span style={{ fontSize: 13, color: T.mute }}>Don't have an account? </span>
              <button onClick={() => { setMode("invite"); setErr(""); setMsg(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: T.accent, fontFamily: SANS }}>Sign Up</button>
            </div>
          </div>
        )}

        {/* INVITE CODE */}
        {mode === "invite" && (
          <div>
            <div style={{ background: T.s2, borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: T.sub, lineHeight: 1.6 }}>
              This app is invite only. Ask the admin for an invite code to create your account.
            </div>
            <input style={{ ...inp, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }} placeholder="INVITE CODE" value={invite} onChange={e => setInvite(e.target.value)} onKeyDown={e => e.key === "Enter" && handleInvite()}/>
            {err && <div style={{ color: "#C4584A", fontSize: 13, marginBottom: 10, padding: "8px 12px", background: "rgba(196,88,74,0.08)", borderRadius: 8 }}>{err}</div>}
            <button onClick={handleInvite} style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", background: T.accent, color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: SANS }}>
              Continue
            </button>
            <div style={{ textAlign: "center", marginTop: 18 }}>
            <button onClick={() => { setMode("login"); setErr(""); setMsg(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.mute, fontFamily: SANS }}>← Back to Sign In</button>
            </div>
          </div>
        )}

        {/* SIGN UP */}
        {mode === "signup" && (
          <div>
            <div style={{ background: "rgba(78,128,96,0.08)", border: "1px solid rgba(78,128,96,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#4E8060", fontWeight: 600 }}>
              ✓ Invite code accepted!
            </div>
            <input style={inp} placeholder="Your name" value={name} onChange={e => setName(e.target.value)}/>
            <input style={inp} placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}/>
            <input style={{ ...inp, marginBottom: 6 }} placeholder="Password (min 6 characters)" type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignUp()}/>
            <div style={{ fontSize: 11, color: T.mute, marginBottom: 14 }}>Use at least 6 characters</div>
            {err && <div style={{ color: "#C4584A", fontSize: 13, marginBottom: 10, padding: "8px 12px", background: "rgba(196,88,74,0.08)", borderRadius: 8 }}>{err}</div>}
            <button disabled={busy} onClick={handleSignUp} style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", background: T.accent, color: "#fff", fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", fontFamily: SANS, opacity: busy ? 0.7 : 1 }}>
              {busy ? "Creating account..." : "Create Account"}
            </button>
            <div style={{ textAlign: "center", marginTop: 18 }}>
              <button onClick={() => { setMode("invite"); setErr(""); setMsg(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.mute, fontFamily: SANS }}>← Back</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
